import React, { PropsWithChildren, createContext, useContext, useMemo } from 'react';
import useTasksHook from '@europrocurement/l2d-modules/modules/Tasks/hook/useTasksHook';
import { DossierTaches } from '@europrocurement/l2d-domain/reducers/formalites/slices/dossierTachesSlice';
import { PrescripteurTaches } from '@europrocurement/l2d-domain/reducers/tiers/slices/prescripteurTachesSlice';
import { UtilisateurTaches } from '@europrocurement/l2d-domain';
import { useCurrentDossierCtx } from './CurrentDossierProvider';

type AnyTask = DossierTaches | PrescripteurTaches | UtilisateurTaches;

export type DossierTasksContextType = {
    orderedTasks: AnyTask[];
    tasksLoading: boolean;
    handleAddTask: () => Promise<void>;
    handleCheckTask: (task: AnyTask) => Promise<void>;
    handleEditTask: (task: AnyTask) => Promise<void>;
    handleUpdatePriority: (task: AnyTask, newPriority: number) => Promise<void>;
    handleDeleteTask: (id: number) => void;
};

const DossierTasksContext = createContext<DossierTasksContextType>({
    orderedTasks: [],
    tasksLoading: false,
    handleAddTask: async () => {},
    handleCheckTask: async () => {},
    handleEditTask: async () => {},
    handleUpdatePriority: async () => {},
    handleDeleteTask: () => {},
});

export const useDossierTasksContext = () => useContext(DossierTasksContext);

const DossierTasksProvider: React.FunctionComponent<PropsWithChildren> = function ({ children }) {
    const { dossier } = useCurrentDossierCtx();

    const {
        orderedTasks,
        tasksLoading,
        handleAddTask,
        handleCheckTask,
        handleEditTask,
        handleUpdatePriority,
        handleDeleteTask,
    } = useTasksHook({
        apiTarget: 'formalite',
        linkedEntityId: dossier ? (dossier.id?.toString() as string) : undefined,
    });

    const contextValue = useMemo(
        () => ({
            orderedTasks,
            tasksLoading,
            handleAddTask,
            handleCheckTask,
            handleEditTask,
            handleUpdatePriority,
            handleDeleteTask,
        }),
        [
            orderedTasks,
            tasksLoading,
            handleAddTask,
            handleCheckTask,
            handleEditTask,
            handleUpdatePriority,
            handleDeleteTask,
        ],
    );

    return (
        <DossierTasksContext.Provider value={contextValue}>{children}</DossierTasksContext.Provider>
    );
};

export default DossierTasksProvider;
